import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { z } from 'zod';
import { VehicleTypes } from '@hero/schema';
import { BrokerIcon, BuildingIcon, TruckIcon, UserIcon } from '@hero/web-icons';
import { PackageIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/Form/Form';
import { Spinner } from '../../Spinner';
import { VehicleTypeMultiSelector } from '../../../feature/Vehicle/VehicleTypeMultiSelector';
import { IconInput } from './IconInput';
import { SelectableCard } from './SelectableCard';

export type CompanyRole = 'business_customer' | 'carrier_company' | 'broker';

export interface CompanyStepValues {
  name: string;
  companyName: string;
  roles: CompanyRole[];
  /** Vehicle types the org operates. Empty unless `carrier_company` is in roles. */
  vehicleTypes: VehicleTypes[];
  /** Self-reported total vehicle count. Optional — only meaningful when `carrier_company` is in roles. */
  totalVehicleCount?: number;
  /** Self-reported expected monthly shipments. Used by any role that books / handles capacity. */
  monthlyExpectedOrder?: number;
}

export interface CompanyStepProps {
  onSubmit: (values: CompanyStepValues) => void | Promise<void>;
  onBack: () => void;
  isPending: boolean;
}

const ROLES: { id: CompanyRole; icon: React.ReactNode }[] = [
  // Carrier first — the carrier-dashboard is the carrier-flagship app,
  // so a fleet-owning org is the most likely first selection.
  { id: 'carrier_company', icon: <TruckIcon size="medium" /> },
  { id: 'business_customer', icon: <UserIcon size="medium" /> },
  { id: 'broker', icon: <BrokerIcon size="medium" /> },
];

/**
 * `overflow-hidden` is required for the height-based collapse
 * animation, which means the input's focus ring (`ring-2
 * ring-offset-2`) gets clipped at the section edges. The inner
 * `px-1 py-1` gives the ring breathing room without changing the
 * visual layout (Tailwind `space-y-*` resets the inner gap).
 */
const sectionMotion = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.2, ease: 'easeOut' },
} as const;

/**
 * Step 2 (Company branch). Collects:
 * - `name`        — display name of the signing-up person
 * - `companyName` — name of the new Org
 * - `roles[]`     — multi-select; subset of `companySignupRoleSchema`
 * - `vehicleTypes[]` + `totalVehicleCount` — only when `carrier_company`
 *   is in roles. Maps to `Org.meta.{vehicleTypes, estimatedVehicleTotal}`.
 * - `monthlyExpectedOrder` — visible whenever any role is selected.
 *   Maps to `Org.meta.estimatedOrdersPerMonth`.
 *
 * The wizard reshapes these into the `signUpSchema` payload before
 * calling `useSignUpWizardManager.submit(...)`.
 */
export function CompanyStep({ onSubmit, onBack, isPending }: CompanyStepProps) {
  const { t } = useTranslation('m-auth');

  const schema = z
    .object({
      name: z.string().trim().min(1, { message: t('form.name.validation') }),
      companyName: z.string().trim().min(1, { message: t('form.company_name.validation') }),
      roles: z
        .array(z.enum(['business_customer', 'carrier_company', 'broker']))
        .min(1, { message: t('form.company_roles.validation') }),
      vehicleTypes: z.array(z.string() as unknown as z.ZodType<VehicleTypes>).default([]),
      // Optional in zod; required-when-carrier is enforced in superRefine
      // so we can give a role-specific error message.
      totalVehicleCount: z.coerce.number().int().min(1).optional(),
      // Same pattern: optional shape, role-specific superRefine below.
      monthlyExpectedOrder: z.coerce.number().int().min(1).optional(),
    })
    .superRefine((data, ctx) => {
      const isCarrier = data.roles.includes('carrier_company');
      if (isCarrier && data.vehicleTypes.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.vehicle_types.validation'),
          path: ['vehicleTypes'],
        });
      }
      if (isCarrier && (data.totalVehicleCount === undefined || data.totalVehicleCount < 1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.total_vehicle_count.validation'),
          path: ['totalVehicleCount'],
        });
      }
      // Any selected role implies an expected monthly volume —
      // broker resells capacity, carrier moves loads, customer ships.
      if (data.roles.length > 0 && (data.monthlyExpectedOrder === undefined || data.monthlyExpectedOrder < 1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.monthly_expected_orders.validation'),
          path: ['monthlyExpectedOrder'],
        });
      }
    });

  const form = useForm<CompanyStepValues>({
    resolver: zodResolver(schema) as never,
    // `onChange` keeps `formState.isValid` live so the Create-account
    // button can stay disabled until every required field passes —
    // including the role-conditional ones (vehicleTypes / totals).
    mode: 'onChange',
    defaultValues: {
      name: '',
      companyName: '',
      roles: [],
      vehicleTypes: [],
      totalVehicleCount: undefined,
      monthlyExpectedOrder: undefined,
    },
  });

  const watchedRoles = form.watch('roles');
  const showVehicleSection = watchedRoles.includes('carrier_company');
  // Any selected role can report monthly volume — broker resells capacity,
  // carrier handles loads, business customer sends shipments.
  const showMonthlyExpected = watchedRoles.length > 0;

  const toggleRole = (current: CompanyRole[], next: CompanyRole): CompanyRole[] => {
    return current.includes(next) ? current.filter(r => r !== next) : [...current, next];
  };

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('company.step_title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('company.step_subtitle')}</p>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.name.title')}</FormLabel>
              <FormControl>
                <IconInput
                  icon={<UserIcon size="small" />}
                  placeholder={t('form.name.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.company_name.title')}</FormLabel>
              <FormControl>
                <IconInput
                  icon={<BuildingIcon size="small" />}
                  placeholder={t('form.company_name.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="roles"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('company.roles_section_title')}</FormLabel>
              <div className="space-y-3">
                {ROLES.map(role => (
                  <SelectableCard
                    key={role.id}
                    selected={field.value.includes(role.id)}
                    onClick={() => field.onChange(toggleRole(field.value, role.id))}
                    title={t(`company.roles.${role.id}.title`)}
                    description={t(`company.roles.${role.id}.description`)}
                    icon={role.icon}
                  />
                ))}
              </div>
              {fieldState.error?.message ? (
                <p className="text-sm font-medium text-destructive" role="alert">
                  {fieldState.error.message}
                </p>
              ) : null}
            </FormItem>
          )}
        />

        <AnimatePresence initial={false}>
          {showVehicleSection ? (
            <motion.div key="vehicles" {...sectionMotion} className="overflow-hidden">
              <div className="space-y-4 px-1 pt-1">
                <FormItem>
                  <FormLabel>{t('vehicles.section_title')}</FormLabel>
                  <FormDescription>{t('vehicles.section_subtitle')}</FormDescription>
                  <Controller
                    control={form.control}
                    name="vehicleTypes"
                    render={({ field, fieldState }) => (
                      <>
                        <VehicleTypeMultiSelector
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('form.vehicle_types.placeholder')}
                        />
                        {fieldState.error?.message ? (
                          <p className="text-sm font-medium text-destructive" role="alert">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                      </>
                    )}
                  />
                </FormItem>

                <FormField
                  control={form.control}
                  name="totalVehicleCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.total_vehicle_count.title')}</FormLabel>
                      <FormControl>
                        <IconInput
                          icon={<TruckIcon size="small" />}
                          type="number"
                          min={0}
                          placeholder={t('form.total_vehicle_count.placeholder')}
                          {...field}
                          value={field.value ?? ''}
                          onChange={e =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t('form.total_vehicle_count.description')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </motion.div>
          ) : null}

          {showMonthlyExpected ? (
            <motion.div key="monthly" {...sectionMotion} className="overflow-hidden">
              <div className="px-1 pt-1">
                <FormField
                  control={form.control}
                  name="monthlyExpectedOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.monthly_expected_orders.title')}</FormLabel>
                      <FormControl>
                        <IconInput
                          icon={<PackageIcon size="small" />}
                          type="number"
                          min={0}
                          placeholder={t('form.monthly_expected_orders.placeholder')}
                          {...field}
                          value={field.value ?? ''}
                          onChange={e =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t('form.monthly_expected_orders.description')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
            {t('buttons.back')}
          </Button>
          <Button
            type="submit"
            disabled={isPending || !form.formState.isValid}
            className="flex-1">
            {isPending ? <Spinner /> : t('buttons.create_account')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
